// System
import React, { useEffect, useMemo, useState } from 'react';

// Components
import { TableBodyEx, TableContainerEx, TableEx } from '@Components/shared/tables/components';
import {
  TableHeadColumnsPresenter,
  TableHeadPresenter,
  TableRowPresenter,
} from '@Components/shared/tables/presenters';
import { LoadingScreen } from '@Components/shared/loaders/LoadingScreen';

// Utils
import { applyColumnVisibility } from '@utils/data/applyColumnVisibility';

//  Table
import { useLocalizationTableColumns } from './useLocalizationsTableColumn';

// Redux
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';

// GraphQL
import { useLocalizationsQuery } from '@Src/generated.graphql';

// Types
import { useLocalization } from '@Modal/hooks/localization/useLocalization';
import {
  Button,
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { localizationsListArray } from '../LocalizationsTitle/LocalizationsListArray';
import { LocalizationItemDto } from '@contracts/api/dto/localization/LocalizationDto';

export const LocalizationsTable: React.FC = () => {
  const CHOOSE_ALL_VALUE = '__choose_all__';
  const [isTableReady, setIsTableReady] = useState(false);
  const [keyFilter, setKeyFilter] = useState('');
  const [valueFilter, setValueFilter] = useState('');

  const {
    localizationsListFilter,
    columnVisibility,
    visiblityCustomizable,
    currentLocalizations,
    handleLocalizationChange,
    handleApplyColumnVisibility,
    handleSetColumnVisibility,
  } = useLocalization();
  const columns = useLocalizationTableColumns();
  const { userPermissions, currentUser } = useAuthorization();
  console.log({ currentUser });
  const canViewLocalizations =
    userPermissions?.includes('CAN_VIEW_LOCALIZATIONS') || currentUser?.isSuper;

  const { data, loading } = useLocalizationsQuery({
    variables: {
      languages: currentLocalizations,
    },
    skip: !canViewLocalizations,
  });

  const visibleColumns = useMemo(
    () => applyColumnVisibility(columns, localizationsListFilter),
    [columns, columnVisibility]
  );

  const parsedData =
    data?.localizations?.translations !== undefined
      ? JSON.parse(data?.localizations?.translations)
      : undefined;

  const filteredData = useMemo(() => {
    if (!parsedData) return [];

    return parsedData.filter((item: any) => {
      const keyMatches = item.key?.toLowerCase().includes(keyFilter.toLowerCase());

      const valueMatches = Object.entries(item)
        .filter(([k]) => k !== 'key')
        .some(([, v]) => {
          if (typeof v === 'string') {
            return v.toLowerCase().includes(valueFilter.toLowerCase());
          }

          if (Array.isArray(v)) {
            return v.some(
              (entry) =>
                typeof entry === 'string' && entry.toLowerCase().includes(valueFilter.toLowerCase())
            );
          }

          return false;
        });

      return keyMatches && valueMatches;
    });
  }, [parsedData, keyFilter, valueFilter]);

  const handleClearFilters = () => {
    setKeyFilter('');
    setValueFilter('');
  };

  const header = useMemo(
    () => (
      <TableHeadPresenter>
        <TableHeadColumnsPresenter columns={visibleColumns} />
      </TableHeadPresenter>
    ),
    [columns]
  );

  useEffect(() => {
    if (!loading && parsedData) {
      const timeout = setTimeout(() => setIsTableReady(true), 300);
      return () => clearTimeout(timeout);
    }
  }, [loading, parsedData]);

  const handleChange = (event: SelectChangeEvent<typeof currentLocalizations>) => {
    const {
      target: { value },
    } = event;
    const arr = value === 'string' ? value.split(',') : value;

    if (arr.includes(CHOOSE_ALL_VALUE)) {
      const isAllSelected = currentLocalizations.length === localizationsListArray.length;

      if (isAllSelected) {
        handleLocalizationChange(['en']);
        handleSetColumnVisibility(['localizations.key', 'localizations.en']);
        return;
      }

      const allLanguages = localizationsListArray.map(({ annotation }) => annotation);
      handleLocalizationChange(allLanguages);
      handleSetColumnVisibility([
        'localizations.key',
        ...allLanguages.map((language) => `localizations.${language}`),
      ]);
      return;
    }

    handleLocalizationChange(arr);
  };

  if (!canViewLocalizations) return <h1>No Permission</h1>;

  return (
    <Stack gap={2} sx={{ marginTop: '8px' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ zIndex: '100' }}
      >
        <Stack direction="row" gap={2}>
          <TextField
            label="Filter by key"
            variant="outlined"
            size="small"
            value={keyFilter}
            onChange={(e) => setKeyFilter(e.target.value)}
          />
          <TextField
            label="Filter by value"
            variant="outlined"
            size="small"
            value={valueFilter}
            onChange={(e) => setValueFilter(e.target.value)}
          />
          <Button
            size="medium"
            variant="contained"
            disabled={keyFilter?.length === 0 && valueFilter?.length === 0}
            onClick={handleClearFilters}
          >
            Clear filters
          </Button>
        </Stack>
        <Select
          labelId="demo-multiple-checkbox-label"
          id="demo-multiple-checkbox"
          multiple
          value={currentLocalizations}
          onChange={handleChange}
          renderValue={(selected) => selected.join(', ')}
          sx={{ width: '215px', height: '40px' }}
        >
          <MenuItem value={CHOOSE_ALL_VALUE}>
            <Checkbox
              checked={currentLocalizations.length === localizationsListArray.length}
              indeterminate={
                currentLocalizations.length > 0 &&
                currentLocalizations.length < localizationsListArray.length
              }
            />
            <ListItemText primary="Choose all" />
          </MenuItem>
          {localizationsListArray.map(({ language, annotation }) => (
            <MenuItem
              key={language}
              value={annotation}
              onClick={() => handleApplyColumnVisibility([`localizations.${annotation}`])}
            >
              <Checkbox checked={currentLocalizations.includes(annotation)} />
              <ListItemText primary={language} />
            </MenuItem>
          ))}
        </Select>
      </Stack>

      {loading || !isTableReady ? (
        <Stack sx={{ position: 'absolute', left: '50%', top: '40%' }}>
          <LoadingScreen />
        </Stack>
      ) : (
        <TableContainerEx sx={{ height: '70vh' }} pageNumber={1}>
          <TableEx name="localizations">
            {header}
            <TableBodyEx>
              {filteredData.map((row: LocalizationItemDto) => (
                <TableRowPresenter
                  id={row.key?.toString() || ''}
                  key={row.key?.toString()}
                  columns={visibleColumns}
                  row={row}
                  onClick={() => {}}
                />
              ))}
            </TableBodyEx>
          </TableEx>
        </TableContainerEx>
      )}
    </Stack>
  );
};
